/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { SVGIcons } from "cms/icons/svg-icons";
import { AddressField } from "cms/fields/fields/field-address";
import { BooleanField } from "cms/fields/fields/field-boolean";
import { ComponentField } from "cms/fields/fields/field-component";
import { CurrencyField } from "cms/fields/fields/field-currency";
import { DateField } from "cms/fields/fields/field-date";
import { DateTimeField } from "cms/fields/fields/field-date-time";
import { DecimalField } from "cms/fields/fields/field-decimal";
import { DocumentField } from "cms/fields/fields/field-document";
import { EmailField } from "cms/fields/fields/field-email";
import { EmbedField } from "cms/fields/fields/field-embed";
import { EmbedBuilderField } from "cms/fields/fields/field-embed-builder";
import { EntityField } from "cms/fields/fields/field-entity";
import { EnumField } from "cms/fields/fields/field-enum";
import { SelectField } from "cms/fields/fields/field-select";
import { HeadingField } from "cms/fields/fields/field-heading";
import { ImageField } from "cms/fields/fields/field-image";
import { JSONField } from "cms/fields/fields/field-json";
import { LinkField } from "cms/fields/fields/field-link";
import { ModelRefField } from "cms/fields/fields/field-model-ref";
import { MultiLineField } from "cms/fields/fields/field-multi-line-text";
import { NumberField } from "cms/fields/fields/field-number";
import { PhoneNumberField } from "cms/fields/fields/field-phone-number";
import { RichTextField } from "cms/fields/fields/field-richtext";
import { TextField } from "cms/fields/fields/field-text";
import { TimeField } from "cms/fields/fields/field-time";
import { TimezoneField } from "cms/fields/fields/field-timezone";
import { UrlField } from "cms/fields/fields/field-url";
import { WebpageRefField } from "cms/fields/fields/field-webpage-ref";
import { NotImplementedField } from "cms/fields/fields/field-not_implemented";
// import { LocationField } from './field-location'

const DEFAULT_STYLE =  "field.style.body";
const DEFAULT_FORMAT =  "field.format.none";
const DEFAULT_SETTINGS = { style: DEFAULT_STYLE, format: DEFAULT_FORMAT, options: {} };

export const fieldTypes = [
    {
        type: "field.address",
        group: "content-model",
        text: "Address",
        icon: SVGIcons.fields.AddressField,
        iconList: SVGIcons.fields.AddressField,
        component: AddressField,
        defaultImpl: {
            value: "",
            settings: {
                style: "field.style.body",
                format: "field.format.none",
                options: {},
            }
        },
        help: {
            notes: [
                "This is an Address Field.",
                "An Address Field contains and address as a text string.",
            ]
        }
    },
    {
        type: "field.boolean",
        group: "content-model",
        text: "Boolean",
        icon: SVGIcons.fields.BooleanField,
        iconList: SVGIcons.fields.BooleanField,
        component: BooleanField,
        defaultImpl: {
            value: false,
            settings: {
                style: "field.style.body",
                format: "field.format.none",
                options: {
                    labelText: "Online"
                },
            }
        },
        help: {
            notes: [
                "This is a Boolean Field.",
                "A Boolean Field contains a true or false value.",
                "Booleans values are often used to drive logic in applications that you build.",
                "You can also configure alternate text to display when the value is true, in the field settings.",
            ]
        },
    },
    {
        type: "field.component",
        group: "content-model",
        text: "Component",
        icon: SVGIcons.fields.ComponentList,
        iconList: SVGIcons.fields.ComponentList,
        component: ComponentField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: [] },
        help: {
            notes: [
                "This is a Component Field.",
                "Component fields give you the flexibility to choose from many component types. This is unlike most other other fields that constrain you to one specific type of content,",
            ]
        },
    },
    {
        type: "field.currency",
        group: "content-model",
        text: "Currency",
        icon: SVGIcons.fields.CurrencyField,
        iconList: SVGIcons.fields.CurrencyField,
        component: CurrencyField,
        defaultImpl: { DEFAULT_SETTINGS, value: 0.00 },
        help: {
            notes: [
                "This is a Currency Field.",
                "It is used to represent a monetary value. ",
                "The default denomination is Canadian Dollars (CDN)"
            ]
        },
    },
    {
        type: "field.date",
        group: "content-model",
        text: "Date",
        icon: SVGIcons.fields.DateOnlyField,
        iconList: SVGIcons.fields.DateOnlyField,
        component: DateField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Date Field.",
                "Use the date picker to select your date.",
                "A Date field is holds a date value only, with no time portion. If you need a Date with Time, use the Datetime field instead.",
            ]
        },
    },
    {
        type: "field.datetime",
        group: "content-model",
        text: "Datetime",
        icon: SVGIcons.fields.DateTimeField,
        iconList: SVGIcons.fields.DateTimeField,
        component: DateTimeField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Date Field.",
                "Use the date/time picker to select your date and time value.",
                "A Datetime field holds a date and time as a single value.",
            ]
        },
    },
    {
        type: "field.decimal",
        group: "content-model",
        text: "Decimal",
        subTitle: "Decimal",
        icon: SVGIcons.fields.DecimalField,
        iconList: SVGIcons.fields.DecimalField,
        component: DecimalField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: 0 },
        help: {
            notes: [
                "This is a Decimal Number Field.",
                "Decimal Number fields only accept numeric digit values, and separate the whole number from the decimal portion",
                "The editor will limit you to only using numeric values, with a set precision.",
            ]
        },
    },
    {
        type: "field.document",
        group: "content-model",
        text: "Document",
        icon: SVGIcons.fields.DocumentField,
        iconList: SVGIcons.fields.DocumentField,
        component: DocumentField,
        defaultImpl: {
            value: "",
            settings: {
                style: DEFAULT_STYLE,
                format: "field.format.document-title-size-icon",
                options: { openIn: "field.target.tab" }
            }
        },
        help: {
            notes: [
                "This is a Document Field.",
                "Use the Document Selector to select a document.",
                "A Document Field is used with documents you upload into in the Digital Assets Document Library.",

            ]
        },
    },
    {
        type: "field.email",
        group: "content-model",
        text: "Email",
        icon: SVGIcons.fields.EmailField,
        iconList: SVGIcons.fields.EmailField,
        component: EmailField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is an Email Field.",
                "An Email Field is a Text field that contains an email address.",
                "The editor will help to constrain you to only add an email address.",
            ]
        },
    },
    {
        type: "field.embed",
        group: "content-model",
        text: "Embed",
        icon: SVGIcons.fields.EmbedField,
        iconList: SVGIcons.fields.EmbedField,
        component: EmbedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is an Embed Field.",
                "Embed Fields only work within webpages and websites.",
                "The field editor will let add a prepared block of HTML.",
            ]
        },
    },
    {
        type: "field.embed-builder",
        group: "content-model",
        text: "Embed Builder",
        icon: SVGIcons.fields.EmbedField,
        iconList: SVGIcons.fields.EmbedField,
        component: EmbedBuilderField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is an Embed Builder Field.",
                "Embed Builder Fields only work within webpages and websites.",
                "The field editor will let you add a uRL and help you to configure an iFrame embed.",
                "If you have a prepared block of HTML to use, choose the Embed Field instead.",
            ]
        },
    },
    {
        type: "field.entity",
        group: "content-model",
        text: "Entity",
        icon: SVGIcons.fields.EnumField,
        iconList: SVGIcons.fields.EnumField,
        component: EntityField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Entity Field. ",
                "Entity Fields act as a container for an entity data record.",
                "Entity Fields are used to select and access,entity records."
            ]
        },
    },
    {
        type: "field.enum",
        group: "content-model",
        text: "Selection",
        icon: SVGIcons.fields.EnumField,
        iconList: SVGIcons.fields.EnumField,
        component: EnumField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Selection Field. ",
                "Selection Fields (also known as a enumeration fields) provide multiple predetermined options to select from.",
                "Dynamic Selection Fields are ones that are configured to get their options from a data source, rather than using predetermined values.",
                "Selections are often used for web dropdown lists."
            ]
        },
    },
    {
        type: "field.select",
        group: "content-model",
        text: "Selection",
        icon: SVGIcons.fields.EnumField,
        iconList: SVGIcons.fields.EnumField,
        component: SelectField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Selection Field. ",
                "Selection Fields provide multiple predetermined options to select from.",
                "Dynamic Selection Fields are ones that are configured to get their options from a data source, rather than using predetermined values.",
                "Selections are often used for web dropdown lists."
            ]
        },
    },
    {
        type: "field.file",
        group: "content-model",
        text: "File",
        icon: SVGIcons.components.Unicorn,
        iconList: SVGIcons.components.Unicorn,
        component: NotImplementedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a File (Attachment) Field.",
                "File Attachments files that don&apos;t fall into Simplicity&apos;s other upload types (documents, images, videos).",
            ]
        },
    },
    {
        type: "field.heading",
        group: "content-model",
        text: "Heading Text",
        icon: SVGIcons.fields.HeadingField,
        iconList: SVGIcons.fields.HeadingField,
        component: HeadingField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: ["This is a Heading field.",
                "You can supply short (single line) textual content.",
                "You can configure a style.",
                "Heading fields can be used with the \"Table of contents\" component to automatically generate in-page navigation.",
            ]
        },
    },
    {
        type: "field.image",
        group: "content-model",
        text: "Image",
        icon: SVGIcons.fields.ImageField,
        iconList: SVGIcons.fields.ImageField,
        component: ImageField,
        defaultImpl: {
            value: "",
            settings: {
                style: DEFAULT_STYLE,
                format: DEFAULT_FORMAT,
                options: {
                    position: "center center",
                    target: "new",
                },
            },
        },
        help: {
            notes: ["This is an Image field.",
                "You can select an image from the digital assets image library",
                "If you need to edit the title and alt tag of the image, you should do that in digital assets.",
                "If this image field is a list, then you can also choose from gallery styles.",
            ]
        },
    },
    {
        type: "field.json",
        group: "content-model",
        text: "JSON",
        icon: SVGIcons.fields.JSONField,
        iconList: SVGIcons.fields.JSONField,
        component: JSONField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a JSON Field.",
                "A JSON Field is used with applications to hold application data as text, using a JSON syntax.",
                "They are not used in regular displayable content.",
            ]
        },
    },
    {
        type: "field.link",
        group: "content-model",
        text: "Link",
        icon: SVGIcons.fields.LinkField,
        iconList: SVGIcons.fields.LinkField,
        component: LinkField,
        defaultImpl: {
            value: "",
            settings: {
                style: DEFAULT_STYLE,
                format: "field.link.text-only", // Notice link vs format here
                options: { openIn: "field.target.tab" }
            }
        },
        help: {
            notes: [
                "This is a Link Field.",
                "A Link Field contains enough information to build a link to a web resource.",
                "Use the Link Selector to find an existing link in SimpliCity, or add a new Link",
                "Links require both link text and a url, at a minimum. You can also add a description, an image, and configure the link target to a new tab (when applicable).",
            ]
        },
    },
    {
        type: "field.location",
        group: "content-model",
        text: "Location",
        icon: SVGIcons.fields.LocationField,
        iconList: SVGIcons.fields.LocationField,
        // component: SIMFieldLocation,
        component: NotImplementedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [""]
        },
    },
    {
        type: "field.map",
        group: "content-model",
        text: "Map",
        icon: SVGIcons.components.Unicorn,
        iconList: SVGIcons.components.Unicorn,
        component: NotImplementedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Map Reference Field.",
                "A Map Reference field points to a map in an external mapping integration.",
                "You can use this to relate a model to an external map.",
            ]
        },
    },
    {
        type: "field.model-ref",
        group: "content-model",
        text: "Model Reference",
        icon: SVGIcons.components.Unicorn,
        iconList: SVGIcons.components.Unicorn,
        component: ModelRefField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Model Reference Field.",
                "A Model Reference field is a reference to another model. ",
                "You can use this to build relationships between models.",
            ]
        },
    },
    {
        type: "field.multi-line-text",
        group: "content-model",
        text: "Multi-line TextField",
        icon: SVGIcons.fields.MultiLineField,
        iconList: SVGIcons.fields.MultiLineField,
        component: MultiLineField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Multi Line Text Field.",
                "This is the same as a simple Text field, except it allows multiple new lines.",
                "If you have many lines or formatted text, you should use Rich Text instead.",
            ]
        },
    },
    {
        type: "field.number",
        group: "content-model",
        text: "Number",
        icon: SVGIcons.fields.NumberField,
        iconList: SVGIcons.fields.NumberField,
        component: NumberField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: 0 },
        help: {
            notes: [
                "This is a Number Field.",
                "Number fields only accept numeric digit values.",
                "The editor will limit you to only using numeric values.",
            ]
        },
    },
    {
        type: "field.phone-number",
        group: "content-model",
        text: "Phone",
        icon: SVGIcons.fields.PhoneNumberField,
        iconList: SVGIcons.fields.PhoneNumberField,
        component: PhoneNumberField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Phone Number Field.",
                "Type in your phone number as text. The editor will attempt to ensure you enter a valid phone number, based on your organizations locale.",
            ]

        },
    },
    {
        type: "field.richtext",
        group: "content-model",
        text: "Rich Text",
        icon: SVGIcons.fields.RichText,
        iconList: SVGIcons.fields.RichText,
        component: RichTextField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: ["This is a Rich Text field.",
                "Rich Text allows you to add multiple paragraphs of formatted text.",
                "Styles are applied within the content, using the style bar.",
                "You can copy and paste suitable formatting.",
                "Rich Text is a flexible way to add longer amounts of textual content.",
            ]
        },
    },
    {
        type: "field.text",
        group: "content-model",
        text: "Text",
        icon: SVGIcons.fields.TextField,
        iconList: SVGIcons.fields.TextField,
        component: TextField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a simple Text Field.",
                "You can supply short (single line) textual content.",
                "You can configure a style."
            ]
        },
    },
    {
        type: "field.time",
        group: "content-model",
        text: "Time",
        icon: SVGIcons.fields.TimeOnlyField,
        iconList: SVGIcons.fields.TimeOnlyField,
        component: TimeField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Time Field.",
                "Use the time picker to select your time value.",
                "A Time field holds only a time value, but not a specific date.",
            ]
        },
    },
    {
        type: "field.timezone",
        group: "content-model",
        text: "Timezone",
        icon: SVGIcons.fields.TimeOnlyField,
        iconList: SVGIcons.fields.TimeOnlyField,
        component: TimezoneField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Timezone Field.",
                "Use the selection to chose your timezone.",
                "By defining a timezone you can translate dates for people who are in different timezones.",
                "The default value for your timezone is set to match the location of your organization.",
            ]
        },
    },
    {
        type: "field.url",
        group: "content-model",
        text: "Url",
        icon: SVGIcons.fields.URLField,
        iconList: SVGIcons.fields.URLField,
        component: UrlField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Url Field.",
                "Type your URL, including the protocol portion at the start. It is advisable to always use https for urls.",
                "A URL is just a raw URL. If you are trying to represent a link with both a url and text, use a Link Field instead.",
            ]
        },
    },
    {
        type: "field.webpage-ref",
        group: "content-model",
        text: "Webpage Reference",
        icon: SVGIcons.components.Unicorn,
        iconList: SVGIcons.components.Unicorn,
        // component: SIMNotImplementedField,
        component: WebpageRefField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Webpage Reference Field.",
                "Use Webpage Reference Fields to points at different webpages in SimpliCity.",
            ]
        },
    },
    {
        type: "field.video",
        group: "content-model",
        text: "Video",
        icon: SVGIcons.components.Unicorn,
        iconList: SVGIcons.components.Unicorn,
        component: NotImplementedField,
        defaultImpl: { settings: DEFAULT_SETTINGS, value: "" },
        help: {
            notes: [
                "This is a Video Field.",
                "Use the Video Selector to select a Video.",
                "A Video Field is used with videos you upload into in the Digital Assets Video Library .",
            ]
        },
    },
    {
        type: "MAGIC",
        group: "text",
        text: "Magic",
        icon: SVGIcons.components.Unicorn,
        iconList: SVGIcons.components.Unicorn,
        disabled: true,
        component: null,
        defaultImpl: {},
        help: {
            notes: ["SimpliCity helps you make magic for your citizens."]
        },
    },
];
